// Variations & SOWs tab — orchestrates the SOW + Variation registers
// inside the combined two-section layout (brand SKILL.md §9.6). Renders
// the shared summary strip with three cards (Total contract value,
// Active SOWs, Pending variations) above the two stacked sections.
//
// State / API wiring follows the meeting tab idiom — `useTrackingItems`
// with the `'sow'` and `'variation'` discriminators drives the lists;
// `useCreateTrackingItem` / `useUpdateTrackingItem` /
// `useArchiveTrackingItem` mutate. Variation status transitions go
// through dedicated approve / reject hooks (NOT useUpdateTrackingItem)
// because the backend Zod refinement rejects status on PUT — the only
// way to flip a variation to approved or rejected is the dedicated
// endpoint, which writes the audit fields server-side.
//
// VariesCell click-to-scroll-and-expand: the variation table passes
// `onNavigateToSow(sowId)` up to this orchestrator, which sets the
// SOW row as expanded + highlighted. The TrackingTable highlight ring
// effect handles the smooth scrollIntoView.
//
// Future work will bring filter chips and the timeline expand affordance
// on ContractValueCard.

import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useSearchParams } from "react-router";
import Alert from "../../../common/Alert";
import LoadingSpinner from "../../../common/LoadingSpinner";
import MetricCard from "../deliverable/MetricCard";
import ArchiveDialog from "../shared/ArchiveDialog";
import { useDeepLinkPageJump } from "../shared/useDeepLinkPageJump";
import ContractValueCard from "./ContractValueCard";

// Lazy-loaded so recharts ships as an on-demand chunk fetched only when the
// contract-value timeline is expanded, not in the eager bundle.
const ContractValueTimeline = lazy(() => import("./ContractValueTimeline"));
import EmptyVariationSowState from "./EmptyVariationSowState";
import SowSection from "./SowSection";
import TrackingPagination from "../shared/TrackingPagination";
import SowTable, { type SowAction } from "./SowTable";
import SowFormModal from "./SowFormModal";
import SowInlineExpand from "./SowInlineExpand";
import SowDetailExpand from "./SowDetailExpand";
import SowFilterChipBar from "./SowFilterChipBar";
import SowActiveFilterSummary from "./SowActiveFilterSummary";
import VariationSection from "./VariationSection";
import VariationTable, { type VariationAction } from "./VariationTable";
import VariationFormModal from "./VariationFormModal";
import VariationInlineExpand from "./VariationInlineExpand";
import VariationDetailExpand from "./VariationDetailExpand";
import VariationFilterChipBar from "./VariationFilterChipBar";
import VariationActiveFilterSummary from "./VariationActiveFilterSummary";
import ApproveVariationDialog, {
  type ApproveVariationPayload,
} from "./ApproveVariationDialog";
import RejectVariationDialog from "./RejectVariationDialog";
import ActivateSowDialog from "./ActivateSowDialog";
import CancelSowDialog from "./CancelSowDialog";
import CorrectSowOriginalValueModal from "./CorrectSowOriginalValueModal";
import CorrectSowStatusModal from "./CorrectSowStatusModal";
import CorrectVariationModal from "./CorrectVariationModal";
import { applySowFilters, emptySowFilters } from "./applySowFilters";
import {
  applyVariationFilters,
  emptyVariationFilters,
} from "./applyVariationFilters";
import {
  activeSowsSubtitle,
  pendingVariationsSubtitle,
} from "./summaryCardSubtitles";
import {
  parseVariationSowUrlState,
  serialiseVariationSowUrlState,
} from "./variationSowUrlState";
import { totalContractValue } from "./contractValueDerivation";
import {
  clearAllocationsIfSingle,
  readTargets,
  stripAllocations,
} from "./variationTargets";
import { useManagedContract } from "../../../../hooks/useManagedContracts";
import {
  useApproveVariation,
  useArchiveTrackingItem,
  useCorrectSowOriginalValue,
  useCorrectSowStatus,
  useCorrectVariation,
  useCreateTrackingItem,
  useRejectVariation,
  useTrackingItems,
  useUpdateTrackingItem,
} from "../../../../hooks/useTracking";
import type {
  CreateSowInput,
  CreateVariationInput,
  Deliverable,
  Milestone,
  Sow,
  TrackingItem,
  UpdateSowInput,
  UpdateVariationInput,
  Variation,
} from "../../../../types";

interface VariationSowTabProps {
  contractId: string;
  itemIdFromUrl?: string | null;
  /** Click-to-navigate from the "Deliverables linked to this SOW"
   *  section in SowDetailExpand. Parent (ManagedContractDetail)
   *  switches the active tab to deliverable + sets ?item=<id>. */
  onNavigateToDeliverable?: (deliverableId: string) => void;
  /** Click-to-navigate to milestone tab. */
  onNavigateToMilestone?: (milestoneId: string) => void;
  /** "Add deliverable to this SOW" button — parent navigates to the
   *  deliverable tab with ?action=create&sowId=<id> so the create
   *  modal opens with the SOW pre-selected. */
  onAddDeliverableToSow?: (sowId: string) => void;
  /** "Add milestone to this SOW" button — mirror. */
  onAddMilestoneToSow?: (sowId: string) => void;
}

/** Section the tab drives expansion against — keeps the two registers'
 *  archive-target state separate so the dialog routes the archive
 *  mutation to the correct tracking type. */
type ArchiveSection = "sow" | "variation";

function isSow(item: TrackingItem | undefined): item is Sow {
  return !!item && item.type === "sow";
}
function isDeliverable(item: TrackingItem | undefined): item is Deliverable {
  return !!item && item.type === "deliverable";
}
function isMilestone(item: TrackingItem | undefined): item is Milestone {
  return !!item && item.type === "milestone";
}
function isVariation(item: TrackingItem | undefined): item is Variation {
  return !!item && item.type === "variation";
}

export default function VariationSowTab({
  contractId,
  itemIdFromUrl,
  onNavigateToDeliverable,
  onNavigateToMilestone,
  onAddDeliverableToSow,
  onAddMilestoneToSow,
}: VariationSowTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseVariationSowUrlState(searchParams),
    [searchParams],
  );

  const sowFilters = urlState.sowFilters;
  const sowSearch = urlState.sowSearch;
  const variationFilters = urlState.variationFilters;
  const variationSearch = urlState.variationSearch;

  const writeUrlState = (next: Partial<typeof urlState>) => {
    const merged = {
      sowFilters: next.sowFilters ?? urlState.sowFilters,
      sowSearch: next.sowSearch ?? urlState.sowSearch,
      variationFilters: next.variationFilters ?? urlState.variationFilters,
      variationSearch: next.variationSearch ?? urlState.variationSearch,
    };
    setSearchParams(serialiseVariationSowUrlState(merged, searchParams), {
      replace: true,
    });
  };

  const includeArchivedSow = sowFilters.includeArchived;
  const includeArchivedVariation = variationFilters.includeArchived;
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [sowModalMode, setSowModalMode] = useState<"create" | null>(null);
  const [variationModalMode, setVariationModalMode] = useState<
    "create" | null
  >(null);
  const [archiveTarget, setArchiveTarget] = useState<{
    section: ArchiveSection;
    item: Sow | Variation;
  } | null>(null);
  const [approveTarget, setApproveTarget] = useState<Variation | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Variation | null>(null);
  const [activateSowTarget, setActivateSowTarget] = useState<Sow | null>(null);
  const [cancelSowTarget, setCancelSowTarget] = useState<Sow | null>(null);
  const [correctSowValueTarget, setCorrectSowValueTarget] = useState<Sow | null>(
    null,
  );
  const [correctSowStatusTarget, setCorrectSowStatusTarget] =
    useState<Sow | null>(null);
  const [correctVariationTarget, setCorrectVariationTarget] =
    useState<Variation | null>(null);
  // View-mode and edit-mode expansions are independent states. Row click
  // toggles view; the pencil/Edit affordance switches view → edit (or
  // opens edit directly). Mirrors the deliverable-tab two-state model so
  // the row interaction is consistent across tracking surfaces.
  const [viewExpandedSowId, setViewExpandedSowId] = useState<string | null>(
    null,
  );
  const [editExpandedSowId, setEditExpandedSowId] = useState<string | null>(
    null,
  );
  const [viewExpandedVariationId, setViewExpandedVariationId] = useState<
    string | null
  >(null);
  const [editExpandedVariationId, setEditExpandedVariationId] = useState<
    string | null
  >(null);
  // Bumped by each section's Clear-all so that section's chip-bar local
  // search-input state forcibly resyncs even when the committed search
  // value didn't visibly change (mid-debounce reset race — the value was
  // already "" before Clear-all fired, so a plain value-prop comparison
  // never observes the reset and a pending debounce can re-land stale text).
  const [sowFilterResetCounter, setSowFilterResetCounter] = useState(0);
  const [variationFilterResetCounter, setVariationFilterResetCounter] =
    useState(0);
  const [highlightSowId, setHighlightSowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  const [highlightVariationId, setHighlightVariationId] = useState<
    string | null
  >(null);

  // Client-side pagination — separate page state per section so filtering
  // one doesn't displace the other. Default page size 25 matches the
  // milestones / meetings / insurance pattern.
  const [sowPage, setSowPage] = useState(1);
  const [sowPageSize, setSowPageSize] = useState(25);
  const [variationPage, setVariationPage] = useState(1);
  const [variationPageSize, setVariationPageSize] = useState(25);

  const contractQuery = useManagedContract(contractId);
  const contract = contractQuery.data;

  // Always fetch with includeArchived=true so the include-archived chip
  // is a pure client-side transform — flipping it never triggers a
  // refetch and never loses other applied filters mid-render.
  const sowQuery = useTrackingItems(contractId, "sow", {
    includeArchived: true,
  });
  const variationQuery = useTrackingItems(contractId, "variation", {
    includeArchived: true,
  });
  // Deliverables + milestones drive the SowDetailExpand "linked items"
  // sections. includeArchived=false here — this is an operational
  // panel, not a history view; archived items belong to the dedicated
  // tabs' include-archived chip.
  const deliverableQuery = useTrackingItems(contractId, "deliverable", {
    limit: 200,
    includeArchived: false,
  });
  const milestoneQuery = useTrackingItems(contractId, "milestone", {
    limit: 200,
    includeArchived: false,
  });

  const sows: Sow[] = useMemo(
    () => (sowQuery.data?.items ?? []).filter(isSow),
    [sowQuery.data],
  );
  const variations: Variation[] = useMemo(
    () => (variationQuery.data?.items ?? []).filter(isVariation),
    [variationQuery.data],
  );
  const deliverables: Deliverable[] = useMemo(
    () => (deliverableQuery.data?.items ?? []).filter(isDeliverable),
    [deliverableQuery.data],
  );
  const milestones: Milestone[] = useMemo(
    () => (milestoneQuery.data?.items ?? []).filter(isMilestone),
    [milestoneQuery.data],
  );

  const filteredSows = useMemo(
    () => applySowFilters(sows, sowFilters, sowSearch),
    [sows, sowFilters, sowSearch],
  );
  const filteredVariations = useMemo(
    () => applyVariationFilters(variations, variationFilters, variationSearch),
    [variations, variationFilters, variationSearch],
  );

  // Slice the filtered sets for the visible page. Page bounds are
  // clamped to the current total so a filter change that shrinks the
  // list back to one page doesn't strand the user on an empty page.
  const sowTotalPages = Math.max(1, Math.ceil(filteredSows.length / sowPageSize));
  const safeSowPage = Math.min(Math.max(1, sowPage), sowTotalPages);
  const pagedSows = useMemo(
    () =>
      filteredSows.slice(
        (safeSowPage - 1) * sowPageSize,
        safeSowPage * sowPageSize,
      ),
    [filteredSows, safeSowPage, sowPageSize],
  );

  const variationTotalPages = Math.max(
    1,
    Math.ceil(filteredVariations.length / variationPageSize),
  );
  const safeVariationPage = Math.min(
    Math.max(1, variationPage),
    variationTotalPages,
  );
  const pagedVariations = useMemo(
    () =>
      filteredVariations.slice(
        (safeVariationPage - 1) * variationPageSize,
        safeVariationPage * variationPageSize,
      ),
    [filteredVariations, safeVariationPage, variationPageSize],
  );

  // Deep-link page-jump: ?item=<id> can target a SOW or a variation. Jump the
  // relevant section's client-side paginator to the page that holds it
  // (otherwise it is highlighted off-screen on page 1), and open the target
  // row's view detail expand so the linked record is readable without a
  // second click. Two independent hooks — exactly one resolves a
  // non-negative index for a given id.
  const sowDeepLinkIndex = useMemo(
    () =>
      itemIdFromUrl
        ? filteredSows.findIndex((s) => s.id === itemIdFromUrl)
        : -1,
    [itemIdFromUrl, filteredSows],
  );
  const variationDeepLinkIndex = useMemo(
    () =>
      itemIdFromUrl
        ? filteredVariations.findIndex((v) => v.id === itemIdFromUrl)
        : -1,
    [itemIdFromUrl, filteredVariations],
  );
  useDeepLinkPageJump({
    itemId: itemIdFromUrl,
    targetIndex: sowDeepLinkIndex,
    pageSize: sowPageSize,
    onJump: (targetPage, _index, itemId) => {
      if (targetPage !== sowPage) setSowPage(targetPage);
      setHighlightSowId(itemId);
      setViewExpandedSowId(itemId);
    },
  });
  useDeepLinkPageJump({
    itemId: itemIdFromUrl,
    targetIndex: variationDeepLinkIndex,
    pageSize: variationPageSize,
    onJump: (targetPage, _index, itemId) => {
      if (targetPage !== variationPage) setVariationPage(targetPage);
      // The initial highlight assumes a SOW; correct it to the variation row.
      setHighlightVariationId(itemId);
      setViewExpandedVariationId(itemId);
    },
  });

  const sowsById = useMemo(() => {
    const m = new Map<string, Sow>();
    for (const s of sows) m.set(s.id, s);
    return m;
  }, [sows]);

  // Derivation. Mirror of the backend `totalContractValue` — the test
  // suite for `contractValueDerivation` exercises the same fixtures as
  // the backend so a formula drift breaks both runtimes.
  const valueBreakdown = useMemo(
    () =>
      totalContractValue(
        { value: contract?.value ?? 0 },
        sows,
        variations,
      ),
    [contract?.value, sows, variations],
  );

  const createMut = useCreateTrackingItem();
  const updateMut = useUpdateTrackingItem();
  const archiveMut = useArchiveTrackingItem();
  const approveMut = useApproveVariation();
  const rejectMut = useRejectVariation();
  const correctSowValueMut = useCorrectSowOriginalValue();
  const correctSowStatusMut = useCorrectSowStatus();
  const correctVariationMut = useCorrectVariation();

  // ─── Summary card derivations ────────────────────────────────────
  const activeSowCount = sows.filter(
    (s) => s.status === "active" && !s.archivedAt,
  ).length;
  const completedSowCount = sows.filter(
    (s) => s.status === "completed" && !s.archivedAt,
  ).length;
  const totalSowCount = sows.filter((s) => !s.archivedAt).length;

  // Pending variations card — proposed only. Card 3 deliberately
  // explicitly counts proposed (rejected is settled, approved is
  // settled).
  const proposedVariations = useMemo(
    () =>
      variations.filter(
        (v) => v.status === "proposed" && !v.archivedAt,
      ),
    [variations],
  );
  const pendingCount = proposedVariations.length;
  const liveVariationCount = useMemo(
    () => variations.filter((v) => !v.archivedAt).length,
    [variations],
  );

  // Subtitles are the brief §6 four-scenario copy. Pulling them through
  // the `summaryCardSubtitles` helper keeps the strings tested without
  // having to mount the orchestrator + React Query stack just to assert
  // a few words in a metric card.
  const activeSubtitle = activeSowsSubtitle({
    totalSowCount,
    completedSowCount,
    hasAnyVariations: liveVariationCount > 0,
  });
  const pendingSubtitle = useMemo(
    () =>
      pendingVariationsSubtitle({
        proposedVariations,
        totalVariationsCount: liveVariationCount,
      }),
    [proposedVariations, liveVariationCount],
  );

  // The variation-side highlight ring lingers ~2s then clears so the
  // next click on the same row re-fires the effect. The SOW-side
  // highlight is driven by deep-link or VariesCell navigation; both
  // clear on the next user interaction (TrackingTable handles the
  // smooth scrollIntoView via the ring effect).
  useEffect(() => {
    if (!highlightVariationId) return;
    const t = window.setTimeout(() => setHighlightVariationId(null), 2000);
    return () => window.clearTimeout(t);
  }, [highlightVariationId]);

  // ─── Handlers ────────────────────────────────────────────────────

  const handleOpenCreateSow = () => setSowModalMode("create");
  const handleCloseSowModal = () => setSowModalMode(null);
  const handleOpenCreateVariation = () => setVariationModalMode("create");
  const handleCloseVariationModal = () => setVariationModalMode(null);

  const handleCreateSow = async (input: CreateSowInput) => {
    const created = (await createMut.mutateAsync({
      contractId,
      type: "sow",
      input,
    })) as Sow | undefined;
    if (created?.id) setHighlightSowId(created.id);
  };

  const handleCreateVariation = async (input: CreateVariationInput) => {
    const created = (await createMut.mutateAsync({
      contractId,
      type: "variation",
      input,
    })) as Variation | undefined;
    if (created?.id) setHighlightVariationId(created.id);
  };

  const handleUpdateSow = (sow: Sow) => async (input: UpdateSowInput) => {
    await updateMut.mutateAsync({
      contractId,
      type: "sow",
      itemId: sow.id,
      input,
    });
  };

  const handleUpdateVariation =
    (variation: Variation) => async (input: UpdateVariationInput) => {
      await updateMut.mutateAsync({
        contractId,
        type: "variation",
        itemId: variation.id,
        input,
      });
    };

  const handleArchiveConfirm = async (reason: string) => {
    if (!archiveTarget) return;
    await archiveMut.mutateAsync({
      contractId,
      type: archiveTarget.section,
      itemId: archiveTarget.item.id,
      reason,
    });
    setArchiveTarget(null);
  };

  const handleApproveConfirm = async (payload: ApproveVariationPayload) => {
    if (!approveTarget) return;
    // The backend hard-rejects approval when the effective date is
    // missing, and the dialog can also adjust the value impact in-place.
    // Persist any change via the standard PUT first — the approve handler
    // re-reads the item, so the version bump is picked up automatically.
    // Skip the PUT when nothing changed to avoid a needless version bump
    // + audit entry.
    const dateChanged = payload.effectiveDate !== approveTarget.effectiveDate;
    const storedValue = approveTarget.valueImpact ?? null;
    const valueChanged =
      payload.valueImpact !== undefined && payload.valueImpact !== storedValue;
    if (dateChanged || valueChanged) {
      const input: UpdateVariationInput = {
        contractId,
        effectiveDate: payload.effectiveDate,
        version: approveTarget.version,
      };
      if (valueChanged) {
        input.valueImpact = payload.valueImpact;
        // Keep the stored targets coherent with the edited value: allocations
        // must sum exactly to valueImpact when present, so a single target
        // drops its redundant allocation (same convention as the edit form)
        // and a non-financial value strips allocations everywhere.
        input.targets =
          payload.valueImpact === null
            ? stripAllocations(readTargets(approveTarget))
            : clearAllocationsIfSingle(readTargets(approveTarget));
      }
      await updateMut.mutateAsync({
        contractId,
        type: "variation",
        itemId: approveTarget.id,
        input,
      });
    }
    await approveMut.mutateAsync({ contractId, itemId: approveTarget.id });
  };

  const handleRejectConfirm = async (reason?: string) => {
    if (!rejectTarget) return;
    await rejectMut.mutateAsync({
      contractId,
      itemId: rejectTarget.id,
      body: reason ? { reason } : {},
    });
  };

  const handleSowAction = (item: Sow, action: SowAction) => {
    if (action === "activate") {
      setActivateSowTarget(item);
      return;
    }
    if (action === "cancel") {
      setCancelSowTarget(item);
      return;
    }
    if (action === "edit") {
      // Pencil action: switch from view-mode to edit-mode (or close
      // edit-mode if the same row is already in edit-mode).
      setViewExpandedSowId(null);
      setEditExpandedSowId((prev) => (prev === item.id ? null : item.id));
      return;
    }
    if (action === "archive") {
      setArchiveTarget({ section: "sow", item });
    }
  };

  const handleActivateSowConfirm = async () => {
    if (!activateSowTarget) return;
    await updateMut.mutateAsync({
      contractId,
      type: "sow",
      itemId: activateSowTarget.id,
      input: {
        contractId,
        status: "active",
        version: activateSowTarget.version,
      },
    });
  };

  const handleCancelSowConfirm = async () => {
    if (!cancelSowTarget) return;
    await updateMut.mutateAsync({
      contractId,
      type: "sow",
      itemId: cancelSowTarget.id,
      input: {
        contractId,
        status: "cancelled",
        version: cancelSowTarget.version,
      },
    });
  };

  const handleVariationAction = (
    item: Variation,
    action: VariationAction,
  ) => {
    if (action === "approve") {
      setApproveTarget(item);
      return;
    }
    if (action === "reject") {
      setRejectTarget(item);
      return;
    }
    if (action === "edit") {
      setViewExpandedVariationId(null);
      setEditExpandedVariationId((prev) =>
        prev === item.id ? null : item.id,
      );
      return;
    }
    if (action === "archive") {
      setArchiveTarget({ section: "variation", item });
    }
  };

  const handleSowRowClick = (item: Sow) => {
    // Row click → view-mode toggle. Same row again collapses; different
    // row moves the expansion. Closes any open edit-mode for clarity.
    setEditExpandedSowId(null);
    setViewExpandedSowId((prev) => (prev === item.id ? null : item.id));
  };

  const handleVariationRowClick = (item: Variation) => {
    setEditExpandedVariationId(null);
    setViewExpandedVariationId((prev) =>
      prev === item.id ? null : item.id,
    );
  };

  const handleNavigateToSow = (sowId: string) => {
    // VariesCell click-to-scroll lands on the read-only detail view —
    // the user is reading, not editing. Edit affordance is one click away.
    setEditExpandedSowId(null);
    setViewExpandedSowId(sowId);
    setHighlightSowId(sowId);
  };

  const handleNavigateToVariation = (variationId: string) => {
    // SOW detail view's "Variations affecting this SOW" row → open the
    // variation's read-only detail expand in its own register. Symmetric
    // with handleNavigateToSow: lands on view-mode (not edit), highlights
    // the row so the user can find it after the scroll, leaves any open
    // SOW expand in place so the user can compare side-by-side.
    setEditExpandedVariationId(null);
    setViewExpandedVariationId(variationId);
    setHighlightVariationId(variationId);
  };

  // Page-level empty state: fires only when both registers are empty
  // and we're not still loading. Per brief §6, partial cases keep both
  // sections visible so the history is reachable; only zero/zero gets
  // the placeholder pair.
  const isFullyEmpty =
    sows.length === 0 &&
    variations.length === 0 &&
    !sowQuery.isPending &&
    !variationQuery.isPending;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ContractValueCard
          contractValue={contract?.value ?? 0}
          sowsValue={valueBreakdown.breakdown.sows}
          variationsValue={valueBreakdown.breakdown.variations}
          gstApplies={contract?.gstApplies}
          hasTimeline={!isFullyEmpty}
          expanded={timelineExpanded}
          onToggleExpanded={() => setTimelineExpanded((v) => !v)}
        />
        <MetricCard
          label="Active SOWs"
          value={activeSowCount}
          valueTone={activeSowCount > 0 ? "success" : "neutral"}
          subtitle={activeSubtitle}
          testId="active-sows-card"
        />
        <MetricCard
          label="Pending variations"
          value={pendingCount}
          valueTone={pendingCount > 0 ? "warning" : "neutral"}
          subtitle={pendingSubtitle}
          testId="pending-variations-card"
        />
      </div>

      {timelineExpanded && !isFullyEmpty && (
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          }
        >
          <ContractValueTimeline
            contract={{
              value: contract?.value ?? 0,
              signedAt: contract?.startDate,
            }}
            sows={sows}
            variations={variations}
            gstApplies={contract?.gstApplies}
          />
        </Suspense>
      )}

      {isFullyEmpty ? (
        <EmptyVariationSowState
          onAddSow={handleOpenCreateSow}
          onAddVariation={handleOpenCreateVariation}
        />
      ) : (
        <>
          <SowSection
            totalCount={sows.length}
            visibleCount={filteredSows.length}
            includeArchived={includeArchivedSow}
            onToggleArchived={(next) =>
              writeUrlState({
                sowFilters: { ...sowFilters, includeArchived: next },
              })
            }
            onAdd={handleOpenCreateSow}
            footer={
              filteredSows.length > 0 ? (
                <TrackingPagination
                  page={safeSowPage}
                  pageSize={sowPageSize}
                  total={filteredSows.length}
                  noun="SOW"
                  testIdPrefix="sow-section"
                  onPageChange={setSowPage}
                  onPageSizeChange={(next) => {
                    setSowPageSize(next);
                    setSowPage(1);
                  }}
                />
              ) : undefined
            }
            filterBar={
              <div className="space-y-2">
                <SowFilterChipBar
                  filters={sowFilters}
                  onFiltersChange={(next) =>
                    writeUrlState({ sowFilters: next })
                  }
                  search={sowSearch}
                  onSearchChange={(next) =>
                    writeUrlState({ sowSearch: next })
                  }
                  resetCounter={sowFilterResetCounter}
                />
                <SowActiveFilterSummary
                  filters={sowFilters}
                  search={sowSearch}
                  onClearAll={() => {
                    writeUrlState({
                      sowFilters: emptySowFilters(),
                      sowSearch: "",
                    });
                    setSowFilterResetCounter((c) => c + 1);
                  }}
                />
              </div>
            }
          >
            {sowQuery.error ? (
              <Alert variant="error">Failed to load SOWs.</Alert>
            ) : (
              <SowTable
                items={pagedSows}
                isLoading={sowQuery.isPending}
                error={sowQuery.error}
                gstApplies={contract?.gstApplies}
                variations={variations}
                onRowClick={handleSowRowClick}
                onAction={handleSowAction}
                highlightRowId={highlightSowId}
                expandedRowId={editExpandedSowId ?? viewExpandedSowId}
                renderExpandedRow={(item) =>
                  editExpandedSowId === item.id ? (
                    <SowInlineExpand
                      key={`edit-${item.id}`}
                      item={item}
                      contractId={contractId}
                      gstApplies={contract?.gstApplies}
                      variations={variations}
                      onUpdate={handleUpdateSow(item)}
                      onCancel={() => setEditExpandedSowId(null)}
                      isSubmitting={updateMut.isPending}
                    />
                  ) : (
                    <SowDetailExpand
                      key={`view-${item.id}`}
                      item={item}
                      gstApplies={contract?.gstApplies}
                      variations={variations}
                      deliverables={deliverables}
                      milestones={milestones}
                      onNavigateToVariation={handleNavigateToVariation}
                      onNavigateToDeliverable={onNavigateToDeliverable}
                      onNavigateToMilestone={onNavigateToMilestone}
                      onAddDeliverableToSow={onAddDeliverableToSow}
                      onAddMilestoneToSow={onAddMilestoneToSow}
                      onEdit={
                        item.archivedAt
                          ? undefined
                          : () => {
                              setViewExpandedSowId(null);
                              setEditExpandedSowId(item.id);
                            }
                      }
                      onCorrectValue={
                        item.archivedAt
                          ? undefined
                          : () => setCorrectSowValueTarget(item)
                      }
                      onCorrectStatus={
                        item.archivedAt
                          ? undefined
                          : () => setCorrectSowStatusTarget(item)
                      }
                    />
                  )
                }
              />
            )}
          </SowSection>

          <VariationSection
            totalCount={variations.length}
            visibleCount={filteredVariations.length}
            includeArchived={includeArchivedVariation}
            onToggleArchived={(next) =>
              writeUrlState({
                variationFilters: {
                  ...variationFilters,
                  includeArchived: next,
                },
              })
            }
            onAdd={handleOpenCreateVariation}
            footer={
              filteredVariations.length > 0 ? (
                <TrackingPagination
                  page={safeVariationPage}
                  pageSize={variationPageSize}
                  total={filteredVariations.length}
                  noun="variation"
                  testIdPrefix="variation-section"
                  onPageChange={setVariationPage}
                  onPageSizeChange={(next) => {
                    setVariationPageSize(next);
                    setVariationPage(1);
                  }}
                />
              ) : undefined
            }
            filterBar={
              <div className="space-y-2">
                <VariationFilterChipBar
                  filters={variationFilters}
                  onFiltersChange={(next) =>
                    writeUrlState({ variationFilters: next })
                  }
                  search={variationSearch}
                  onSearchChange={(next) =>
                    writeUrlState({ variationSearch: next })
                  }
                  sows={sows}
                  resetCounter={variationFilterResetCounter}
                />
                <VariationActiveFilterSummary
                  filters={variationFilters}
                  search={variationSearch}
                  sows={sows}
                  onClearAll={() => {
                    writeUrlState({
                      variationFilters: emptyVariationFilters(),
                      variationSearch: "",
                    });
                    setVariationFilterResetCounter((c) => c + 1);
                  }}
                />
              </div>
            }
          >
            {variationQuery.error ? (
              <Alert variant="error">Failed to load variations.</Alert>
            ) : (
              <VariationTable
                items={pagedVariations}
                isLoading={variationQuery.isPending}
                error={variationQuery.error}
                gstApplies={contract?.gstApplies}
                sowsById={sowsById}
                onNavigateToSow={handleNavigateToSow}
                onRowClick={handleVariationRowClick}
                onAction={handleVariationAction}
                highlightRowId={highlightVariationId}
                expandedRowId={
                  editExpandedVariationId ?? viewExpandedVariationId
                }
                renderExpandedRow={(item) =>
                  editExpandedVariationId === item.id ? (
                    <VariationInlineExpand
                      key={`edit-${item.id}`}
                      item={item}
                      contractId={contractId}
                      gstApplies={contract?.gstApplies}
                      sowsById={sowsById}
                      sows={sows}
                      onUpdate={handleUpdateVariation(item)}
                      onCancel={() => setEditExpandedVariationId(null)}
                      isSubmitting={updateMut.isPending}
                    />
                  ) : (
                    <VariationDetailExpand
                      key={`view-${item.id}`}
                      item={item}
                      gstApplies={contract?.gstApplies}
                      sowsById={sowsById}
                      onEdit={
                        item.archivedAt
                          ? undefined
                          : () => {
                              setViewExpandedVariationId(null);
                              setEditExpandedVariationId(item.id);
                            }
                      }
                      onCorrect={
                        item.archivedAt
                          ? undefined
                          : () => setCorrectVariationTarget(item)
                      }
                    />
                  )
                }
              />
            )}
          </VariationSection>
        </>
      )}

      <SowFormModal
        isOpen={sowModalMode === "create"}
        onClose={handleCloseSowModal}
        mode="create"
        contractId={contractId}
        gstApplies={contract?.gstApplies}
        existingSows={sows}
        numberFormat={contract?.sowNumberFormat ?? "nn"}
        onCreate={handleCreateSow}
        isSubmitting={createMut.isPending}
      />

      <VariationFormModal
        isOpen={variationModalMode === "create"}
        onClose={handleCloseVariationModal}
        mode="create"
        contractId={contractId}
        gstApplies={contract?.gstApplies}
        sows={sows}
        existingVariations={variations}
        numberFormat={contract?.variationNumberFormat ?? "nn"}
        onCreate={handleCreateVariation}
        isSubmitting={createMut.isPending}
      />

      <ArchiveDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        itemLabel={
          archiveTarget
            ? archiveTarget.section === "sow"
              ? `SOW "${archiveTarget.item.name}"`
              : `variation "${archiveTarget.item.name}"`
            : "this item"
        }
        isSubmitting={archiveMut.isPending}
      />

      <ApproveVariationDialog
        isOpen={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        variation={approveTarget}
        sowsById={sowsById}
        gstApplies={contract?.gstApplies}
        onConfirm={handleApproveConfirm}
        isSubmitting={approveMut.isPending || updateMut.isPending}
      />

      <RejectVariationDialog
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        variation={rejectTarget}
        sowsById={sowsById}
        gstApplies={contract?.gstApplies}
        onConfirm={handleRejectConfirm}
        isSubmitting={rejectMut.isPending}
      />

      <ActivateSowDialog
        isOpen={!!activateSowTarget}
        onClose={() => setActivateSowTarget(null)}
        sow={activateSowTarget}
        gstApplies={contract?.gstApplies}
        onConfirm={handleActivateSowConfirm}
        isSubmitting={updateMut.isPending}
      />

      <CancelSowDialog
        isOpen={!!cancelSowTarget}
        onClose={() => setCancelSowTarget(null)}
        sow={cancelSowTarget}
        gstApplies={contract?.gstApplies}
        onConfirm={handleCancelSowConfirm}
        isSubmitting={updateMut.isPending}
      />

      <CorrectSowOriginalValueModal
        isOpen={!!correctSowValueTarget}
        onClose={() => setCorrectSowValueTarget(null)}
        sow={correctSowValueTarget}
        gstApplies={contract?.gstApplies}
        onConfirm={async (newOriginalValue, reason) => {
          if (!correctSowValueTarget) return;
          await correctSowValueMut.mutateAsync({
            contractId,
            itemId: correctSowValueTarget.id,
            body: { newOriginalValue, reason },
          });
        }}
        isSubmitting={correctSowValueMut.isPending}
      />

      <CorrectSowStatusModal
        isOpen={!!correctSowStatusTarget}
        onClose={() => setCorrectSowStatusTarget(null)}
        sow={correctSowStatusTarget}
        onConfirm={async (newStatus, reason) => {
          if (!correctSowStatusTarget) return;
          await correctSowStatusMut.mutateAsync({
            contractId,
            itemId: correctSowStatusTarget.id,
            body: { newStatus, reason },
          });
        }}
        isSubmitting={correctSowStatusMut.isPending}
      />

      <CorrectVariationModal
        isOpen={!!correctVariationTarget}
        onClose={() => setCorrectVariationTarget(null)}
        variation={correctVariationTarget}
        gstApplies={contract?.gstApplies}
        onConfirm={async (body) => {
          if (!correctVariationTarget) return;
          await correctVariationMut.mutateAsync({
            contractId,
            itemId: correctVariationTarget.id,
            body,
          });
        }}
        isSubmitting={correctVariationMut.isPending}
      />
    </div>
  );
}
